import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createSwitchOnOffState,
  deleteSwitchOnOffState,
  getSwitchOnOffStateById,
  getSwitchOnOffStatesList,
  updateSwitchOnOffState,
  updateSwitchOnOffStateStatus,
  type SwitchOnOffStateListParams,
  type SwitchOnOffStatePayload,
  type SwitchOnOffStateStatusPayload,
} from "src/services/administrator/switchOnOffStateService";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";

export const useQueryGetSwitchOnOffStatesList = (
  params: SwitchOnOffStateListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.switchOnOffStateList, params, language],
      queryFn: () => getSwitchOnOffStatesList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false,
  );
};

export const useQueryGetSwitchOnOffStateById = (
  id?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.switchOnOffStateDetail, id, language],
    queryFn: () => getSwitchOnOffStateById(id!, language),
    enabled: Boolean(id) && options.enabled !== false,
    refetchOnMount: "always",
    retry: false,
    staleTime: 0,
    ...options,
  });
};

export const useMutationCreateSwitchOnOffState = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: SwitchOnOffStatePayload) =>
      createSwitchOnOffState(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.switchOnOffStateList,
      }),
  });
};

export const useMutationUpdateSwitchOnOffState = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: SwitchOnOffStatePayload & { id: string | number };
    }) => updateSwitchOnOffState(id, payload, language),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: QueryNames.switchOnOffStateList,
        }),
        queryClient.invalidateQueries({
          queryKey: QueryNames.switchOnOffStateDetail,
          refetchType: "none",
        }),
      ]),
  });
};

export const useMutationUpdateSwitchOnOffStateStatus = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      active,
    }: SwitchOnOffStateStatusPayload) =>
      updateSwitchOnOffStateStatus(id, { id, active }, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.switchOnOffStateList,
      }),
  });
};

export const useMutationDeleteSwitchOnOffState = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) =>
      deleteSwitchOnOffState(id, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.switchOnOffStateList,
      }),
  });
};
